import { EnumType } from 'json-to-graphql-query';
import deparam from 'deparam';
import {
  currentPriceFilterName,
  manufacturerFilterName,
  modelVariantFilterName,
  manufacturerModelFilterName,
  financesTermFilterName,
  financesDepositFilterName,
  financesAnnualMileageFilterName,
  financesPaymentFilterName,
  variantFilterName,
  modelFilterName,
} from './filterNames';
import search from './search';
import queryBuilder from './query-builder';
import filterAttributesQueries from './attributes';
import { locationAttributes } from './location-attribute';

export default {
  async getCount({ criteria, clientToken, uuid, apiUrl }) {
    const query = {
      query: {
        getCount: queryBuilder.getCountQuery({ criteria }),
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getVehiclesWithCount({
    criteria,
    currentPage,
    pageSize,
    sortParams,
    fields,
    clientToken,
    uuid,
    apiUrl,
    financesDataContext,
    newFinancesDataContext,
    stockScope,
  }) {
    const query = {
      query: {
        getCount: queryBuilder.getCountQuery({ criteria }),
        allVehicles: {
          __aliasFor: 'getAll',
          ...queryBuilder.getVehiclesQuery({
            criteria,
            currentPage,
            pageSize,
            sortParams,
            fields,
            financesDataContext,
            newFinancesDataContext,
          }),
        },
        offerVehicles: {
          ...queryBuilder.getOffersQuery({
            stockScope,
            currentPage,
            pageSize,
            sortParams,
          }),
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getVehicle({ criteria, currentPage, pageSize, fields, clientToken, uuid, apiUrl }) {
    const query = {
      query: {
        getAll: queryBuilder.getVehiclesQuery({
          criteria,
          currentPage,
          pageSize,
          fields,
        }),
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getVehiclesWithFilterOptions({
    criteria,
    currentPage,
    pageSize,
    sortParams,
    fields,
    clientToken,
    uuid,
    apiUrl,
    financesDataContext,
    newFinancesDataContext,
    stockScope,
  }) {
    const query = {
      query: {
        getCount: queryBuilder.getCountQuery({ criteria }),
        allVehicles: {
          __aliasFor: 'getAll',
          ...queryBuilder.getVehiclesQuery({
            criteria,
            currentPage,
            pageSize,
            sortParams,
            fields,
            financesDataContext,
            newFinancesDataContext,
          }),
        },
        offerVehicles: {
          ...queryBuilder.getOffersQuery({
            stockScope,
            currentPage,
            pageSize,
            sortParams,
          }),
        },
        getFilterAttributes: {
          ...queryBuilder.getVehiclesQuery({ criteria: stockScope }),
          ...filterAttributesQueries,
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getVehicles({
    criteria,
    currentPage,
    pageSize,
    sortParams,
    fields,
    clientToken,
    uuid,
    apiUrl,
    financesDataContext,
    newFinancesDataContext,
    stockScope,
  }) {
    const query = {
      query: {
        allVehicles: {
          __aliasFor: 'getAll',
          ...queryBuilder.getVehiclesQuery({
            criteria,
            currentPage,
            pageSize,
            sortParams,
            fields,
            financesDataContext,
            newFinancesDataContext,
          }),
        },
        offerVehicles: {
          ...queryBuilder.getOffersQuery({
            stockScope,
            currentPage,
            pageSize,
            sortParams,
          }),
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getFiltersOptions({ criteria, clientToken, uuid, apiUrl }) {
    const query = {
      query: {
        getFilterAttributes: {
          ...queryBuilder.getVehiclesQuery({ criteria }),
          ...filterAttributesQueries,
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },

  async getKeywordSearch({ clientToken, uuid, apiUrl, keyword, stockScope }) {
    const query = {
      query: {
        getFilterAttributes: {
          __args: {
            searchParams: {
              ...stockScope,
              freeText: {
                query: keyword,
              },
            },
          },
          manufacturerModel: {
            items: {
              count: true,
              label: true,
              models: {
                count: true,
                label: true,
              },
            },
          },
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },

  async getPostcodeCoordinates({ location, country, clientToken, uuid, apiUrl }) {
    const query = {
      query: {
        getPostcodeCoordinates: {
          __args: {
            country,
            postcode: location,
          },
          lat: true,
          lon: true,
        },
      },
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getFiltersOptionsCounts({ criteria, defaultCriteria, clientToken, uuid, apiUrl }) {
    const createdQueries = {};
    // clone queries so we can remove the current filter item
    const queries = { ...filterAttributesQueries };

    // remove filters that need specific query logic
    delete queries[financesTermFilterName];
    delete queries[financesDepositFilterName];
    delete queries[financesAnnualMileageFilterName];
    delete queries[financesPaymentFilterName];
    delete queries[currentPriceFilterName];
    delete queries[manufacturerFilterName];
    delete queries[manufacturerModelFilterName];
    delete queries[modelVariantFilterName];

    const searchParamsNames = Object.keys(criteria);
    let count = 0;

    Object.keys(queries).forEach((key) => {
      // if the query name does not exist in the search params then exit
      if (!searchParamsNames.includes(key)) return;
      // store current index for later
      const { [key]: _, ...searchCriteria } = criteria;

      // remove the active filter from the object
      delete queries[key];

      // add seperate query for active filter
      createdQueries[`count${count}`] = {
        __aliasFor: 'getFilterAttributes',
        ...queryBuilder.getVehiclesQuery({ criteria: searchCriteria }),
        [key]: filterAttributesQueries[key],
      };

      count += 1;
    });

    // add final query for remaining filters remove currentPrice
    createdQueries[`count${count}`] = {
      __aliasFor: 'getFilterAttributes',
      ...queryBuilder.getVehiclesQuery({ criteria }),
      ...queries,
    };

    // get count manufacturer filter
    const {
      [manufacturerFilterName]: a,
      [modelFilterName]: b,
      [variantFilterName]: c,
      ...searchCriteriaManufacturer
    } = { ...criteria };

    createdQueries[`count${count + 1}`] = {
      __aliasFor: 'getFilterAttributes',
      ...queryBuilder.getVehiclesQuery({
        criteria: { ...searchCriteriaManufacturer, ...defaultCriteria },
      }),
      [manufacturerFilterName]: filterAttributesQueries[manufacturerFilterName],
    };

    // get count model filter
    const { [modelFilterName]: d, [variantFilterName]: e, ...searchCriteriaModel } = {
      ...criteria,
    };

    createdQueries[`count${count + 2}`] = {
      __aliasFor: 'getFilterAttributes',
      ...queryBuilder.getVehiclesQuery({
        criteria: { ...searchCriteriaModel, ...defaultCriteria },
      }),
      [manufacturerModelFilterName]: filterAttributesQueries[manufacturerModelFilterName],
    };

    // get count variant filter
    const { [variantFilterName]: f, ...searchCriteriaVariant } = { ...criteria };

    createdQueries[`count${count + 3}`] = {
      __aliasFor: 'getFilterAttributes',
      ...queryBuilder.getVehiclesQuery({
        criteria: { ...searchCriteriaVariant, ...defaultCriteria },
      }),
      [modelVariantFilterName]: filterAttributesQueries[modelVariantFilterName],
    };

    // add price values
    createdQueries[`count${count + 4}`] = {
      __aliasFor: 'getFilterAttributes',
      ...queryBuilder.getVehiclesQuery({ criteria: defaultCriteria }),
      [currentPriceFilterName]: filterAttributesQueries[currentPriceFilterName],
      [financesTermFilterName]: filterAttributesQueries[financesTermFilterName],
      [financesDepositFilterName]: filterAttributesQueries[financesDepositFilterName],
      [financesAnnualMileageFilterName]: filterAttributesQueries[financesAnnualMileageFilterName],
      [financesPaymentFilterName]: filterAttributesQueries[financesPaymentFilterName],
    };

    const query = {
      query: createdQueries,
    };

    return search({ query, clientToken, uuid, apiUrl });
  },
  getQuerySearch() {
    const { search: params } = window.location;
    if (!params) return {};
    const string = new URLSearchParams(params).toString();
    if (!string) return {};

    const { currentPage, sortParams, location, nfcSearchVersion, ...searchParams } = deparam(
      string,
    );

    return { currentPage, sortParams, location, searchParams, nfcSearchVersion };
  },

  // !TODO need to be refactor to use helper functions in query builder
  getAllLocations({
    uuid,
    clientToken,
    apiUrl,
    distanceFrom,
    sortParams = {},
    searchParams = {},
    pagination = {},
  }) {
    const query = {
      query: {
        getAllLocations: {
          __args: {
            distanceFrom,
          },
          ...locationAttributes,
        },
      },
    };

    const newSortParams = Object.entries(sortParams).reduce((accu, [key, value]) => {
      if (key === 'fieldName' || key === 'direction') {
        return {
          ...accu,
          [key]: new EnumType(value),
        };
      }
      if (key === 'distanceDetails') {
        return {
          ...accu,
          [key]: {
            unit: new EnumType(sortParams[key].unit),
            coordinates: distanceFrom.coordinates,
          },
        };
      }
      return { ...accu };
    }, {});

    if (Object.keys(newSortParams).length !== 0) {
      query.query.getAllLocations.__args = {
        ...query.query.getAllLocations.__args,
        sortParams: newSortParams,
      };
    }
    if (Object.keys(searchParams).length !== 0) {
      query.query.getAllLocations.__args = {
        ...query.query.getAllLocations.__args,
        searchParams,
      };
    }
    if (Object.keys(pagination).length !== 0) {
      query.query.getAllLocations.__args = {
        ...query.query.getAllLocations.__args,
        pagination,
      };
    }

    return search({ query, clientToken, uuid, apiUrl });
  },
};
